import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { ROUTES } from "@via/utils/constants.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";
import {
  CreateAccountRequestBody,
  CreateAccountRequestQuery,
  ForwardToSalesRequestBody,
  MfaRequiredResponse,
  RegisterRequestBody,
  VerifyEmailRequestBody,
} from "common";
import { AgreeUserAgreementQueryParams, UserAgreementResponse } from "common";
import { useNavigate } from "react-router-dom";

export const fetchMandatoryAgreements =
  async (): Promise<UserAgreementResponse> => {
    try {
      const res = await axios.get("/api/v1/agreement/mandatory");
      return res.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

export const useGetMandatoryUserAgreementsHandler = (
  options: { enabled?: boolean } = {}
) => {
  return useQuery<UserAgreementResponse>({
    queryKey: ["/api/v1/agreement/mandatory"],
    queryFn: fetchMandatoryAgreements,
    retry: false,
    enabled: options.enabled ?? true,
  });
};

export const useAgreeUserAgreement = (): UseMutationResult<
  void,
  ErrorNotification,
  AgreeUserAgreementQueryParams
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (payload: AgreeUserAgreementQueryParams) => {
      await axios.post(`/api/v1/agreement/${payload.id}/agree`);
    },
    onSuccess: (): void => {},
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "user-agreement-failed",
        message: "User Agreement Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useRegisterUrlInvite = (): UseMutationResult<
  void,
  ErrorNotification,
  RegisterRequestBody & { isResend?: boolean }
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (
      payload: RegisterRequestBody & { isResend?: boolean }
    ) => {
      const { isResend, ...requestBody } = payload;
      await axios.post("/api/auth/v1/register-url-invite", requestBody);
    },
    onSuccess: (_data, variables): void => {
      if (variables.isResend) {
        setNotification({
          id: "code-resend",
          message: "Verification Code Resent",
          details: "Check your inbox (and spam folder) for the new code.",
          variant: NOTIFICATION.SUCCESS,
        });
      }
    },
    onError: (error: ErrorNotification) => {
      const data = error?.response?.data;
      const details =
        typeof data === "string"
          ? data
          : ((data as { message?: string } | undefined)?.message ??
            "Contact support if the issue persists");
      setNotification({
        id: "email-register-failed",
        message: "Email Register Failed",
        details,
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useRegister = (): UseMutationResult<
  void,
  ErrorNotification,
  RegisterRequestBody & { isResend?: boolean }
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (
      payload: RegisterRequestBody & { isResend?: boolean }
    ) => {
      const { isResend, ...requestBody } = payload;
      await axios.post("/api/auth/v1/register", requestBody);
    },
    onSuccess: (_data, variables): void => {
      if (variables.isResend) {
        setNotification({
          id: "code-resend",
          message: "Verification Code Resent",
          details: "Check your inbox (and spam folder) for the new code.",
          variant: NOTIFICATION.SUCCESS,
        });
      }
    },
    onError: (error: ErrorNotification) => {
      const data = error?.response?.data;
      const details =
        typeof data === "string"
          ? data
          : ((data as { message?: string } | undefined)?.message ??
            "Contact support if the issue persists");
      setNotification({
        id: "email-register-failed",
        message: "Email Register Failed",
        details,
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useVerifyEmail = (): UseMutationResult<
  void,
  ErrorNotification,
  VerifyEmailRequestBody
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (payload: VerifyEmailRequestBody) => {
      const response = await axios.post("/api/auth/v1/verify-email", payload);
      return response.data;
    },
    onSuccess: (): void => {},
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "email-verification-failed",
        message: "Email Verification Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useForwardToSales = (): UseMutationResult<
  void,
  ErrorNotification,
  ForwardToSalesRequestBody
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: ForwardToSalesRequestBody) => {
      const response = await axios.post(
        "/api/auth/v1/forward-to-sales",
        payload
      );
      return response.data;
    },
    onSuccess: (): void => {},
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "forward-sales-failed",
        message: "Registration Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useCreateAccount = (): UseMutationResult<
  MfaRequiredResponse | undefined,
  ErrorNotification,
  {
    requestQueryParams: CreateAccountRequestQuery;
    requestBody: CreateAccountRequestBody;
  }
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (payload: {
      requestQueryParams: CreateAccountRequestQuery;
      requestBody: CreateAccountRequestBody;
    }): Promise<MfaRequiredResponse | undefined> => {
      return (
        await axios.post("/api/auth/v1/create-account", payload.requestBody, {
          params: payload.requestQueryParams,
        })
      ).data;
    },
    onSuccess: async (data): Promise<void> => {
      // Under MFA the account exists but no session does until the code screen.
      if (data?.mfaRequired) {
        navigate(ROUTES.LOGIN_MFA);
        return;
      }
      await queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/status"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "create-account-failed",
        message: "Account Creation Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};
